import Link from 'next/link'
import { useRouter } from 'next/router'

import { ConnectPill } from '@/design-system/ConnectPill'
import { ProductLauncher } from '@/design-system/ProductLauncher'
import { useTheme } from '@/design-system/ThemeProvider'
import { ThemeToggle } from '@/design-system/ThemeToggle'
import { FOCUS } from '@/design-system/ui'
import { useTotalTvl } from '@/hooks/useTotalTvl'
import { compactNumber } from '@/utils/numberFormatter'

/// The SeaFi Treasury app shell: a floating white pill header on the grey canvas — Vaults logo on
/// the left, segmented nav (Vaults · Docs · Analytics) centred, connect + theme cluster on the
/// right — a centred content column capped at 1328px, and a hairline footer. Scrolls horizontally on
/// narrow viewports so no link is dropped. Public model: no auth / sign-out.

type Item = { href: string; label: string; external?: boolean; short?: string }

// SeaFi isn't a plain nav link — it's the product launcher (dropdown), appended after these in
// both the desktop nav and the mobile bottom nav.
const ITEMS: Item[] = [
  { href: '/', label: 'Vaults' },
  { href: '/docs', label: 'Docs' },
  { href: 'https://defillama.com/protocol/seafi', label: 'Analytics', external: true },
]

function isActive(pathname: string, href: string): boolean {
  if (href === '/') return pathname === '/' || pathname === '/[token]'
  return pathname === href || pathname.startsWith(`${href}/`)
}

// Icons for the mobile bottom nav.
function NavIcon({ label }: { label: string }) {
  const p = {
    'aria-hidden': true,
    'fill': 'none',
    'height': 24,
    'stroke': 'currentColor',
    'strokeLinecap': 'round' as const,
    'strokeLinejoin': 'round' as const,
    'strokeWidth': 1.7,
    'viewBox': '0 0 24 24',
    'width': 24,
  }
  if (label === 'Docs') {
    return (
      <svg {...p}>
        <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
        <path d="M14 3v5h5M9 13h6M9 17h6" />
      </svg>
    )
  }
  if (label === 'Analytics') {
    return (
      <svg {...p}>
        <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
      </svg>
    )
  }
  if (label === 'SeaFi') {
    // App-switcher grid — the SeaFi product suite.
    return (
      <svg {...p}>
        <rect height="7" rx="1.5" width="7" x="3" y="3" />
        <rect height="7" rx="1.5" width="7" x="14" y="3" />
        <rect height="7" rx="1.5" width="7" x="3" y="14" />
        <rect height="7" rx="1.5" width="7" x="14" y="14" />
      </svg>
    )
  }
  // Vaults — a vault door
  return (
    <svg {...p}>
      <rect height="16" rx="2" width="18" x="3" y="4" />
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 4v2M12 20v-2" />
    </svg>
  )
}

const container = {
  width: 'min(var(--maxw), calc(100% - var(--app-gutter) * 2))',
} as const

export function AppShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useRouter()
  const { resolved } = useTheme()
  // The wordmark ships in ink; swap to the white variant on dark surfaces (the red isotipo is kept
  // in both). The brand red stays constant across themes.
  const logoSrc =
    resolved === 'dark'
      ? '/assets/img/nav/logo-vaults-white.svg'
      : '/assets/img/nav/logo-vaults.svg'
  const { tvlUsd } = useTotalTvl()
  const tvlText = tvlUsd == null ? '—' : `$${compactNumber(tvlUsd)}`

  return (
    <div
      className="seafi-ds app-shell flex min-h-screen flex-col"
      style={{ background: 'var(--canvas)' }}
    >
      <header className="app-hd">
        <div className="app-pill">
          {/* logo — Vaults by SeaFi, home = vaults */}
          <Link aria-label="Vaults, home" className={`app-logo no-underline ${FOCUS}`} href="/">
            <img
              alt="Vaults"
              height={36}
              src={logoSrc}
              style={{ display: 'block', height: 36, width: 'auto' }}
            />
          </Link>

          {/* segmented nav — SeaFi is rendered as the product launcher (dropdown), not a link */}
          <nav aria-label="Primary" className="app-nav">
            {ITEMS.map((item) =>
              item.external ? (
                <a
                  className={`app-seg ${FOCUS}`}
                  href={item.href}
                  key={item.href}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  aria-current={isActive(pathname, item.href) ? 'page' : undefined}
                  className={`app-seg ${isActive(pathname, item.href) ? 'is-active' : ''} ${FOCUS}`}
                  href={item.href}
                  key={item.href}
                >
                  {item.label}
                </Link>
              ),
            )}
            <ProductLauncher />
          </nav>

          {/* account cluster — logo · TVL (desktop) · connect · theme toggle */}
          <div className="app-account">
            <div className="app-tvl" title="Total value locked">
              <span className="app-tvl-label">TVL</span>
              <span className="app-tvl-value tabular-nums">{tvlText}</span>
            </div>
            <ThemeToggle />
            <ConnectPill />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full flex-1" style={{ ...container, padding: '16px 0 56px' }}>
        {children}
      </main>

      {/* Mobile bottom navigation — Vaults · Docs · Analytics · SeaFi (product launcher). */}
      <nav aria-label="Primary" className="app-bottomnav">
        {ITEMS.map((item) => {
          const active = !item.external && isActive(pathname, item.href)
          const shortLabel = item.short ?? item.label
          const inner = (
            <>
              <NavIcon label={shortLabel} />
              <span>{shortLabel}</span>
            </>
          )
          return item.external ? (
            <a
              className="app-bottomnav-item"
              href={item.href}
              key={item.href}
              rel="noopener noreferrer"
              target="_blank"
            >
              {inner}
            </a>
          ) : (
            <Link
              aria-current={active ? 'page' : undefined}
              className={`app-bottomnav-item ${active ? 'is-active' : ''}`}
              href={item.href}
              key={item.href}
            >
              {inner}
            </Link>
          )
        })}
        <ProductLauncher variant="dock" />
      </nav>
    </div>
  )
}
